import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    ChevronDown,
    ChevronUp,
    MessageCircle,
    Shield,
    Clock,
    Settings,
    CreditCard,
    Dog,
    HeartPulse,
    PawPrint,
    Sparkles,
    Search,
    Heart,
    CheckCircle2,
    HelpCircle,
    Info
} from 'lucide-react'
import { Button } from "@/components/ui/button"

const FAQSection = () => {
    const [selectedCategory, setSelectedCategory] = useState('general');
    const [searchQuery, setSearchQuery] = useState('');
    const [openFAQs, setOpenFAQs] = useState({});

    const categories = [
        { 
            id: 'general', 
            label: 'Información General', 
            icon: Info,
            color: "from-blue-500 to-blue-600",
            description: "Funcionamiento básico de la plataforma"
        },
        { 
            id: 'tech', 
            label: 'Tecnología', 
            icon: Settings,
            color: "from-blue-500 to-cyan-500",
            description: "Especificaciones técnicas del sistema"
        },
        { 
            id: 'billing', 
            label: 'Planes y Pagos', 
            icon: CreditCard,
            color: "from-green-500 to-emerald-500",
            description: "Opciones de suscripción disponibles"
        },
        { 
            id: 'medical', 
            label: 'Información Veterinaria', 
            icon: HeartPulse,
            color: "from-purple-500 to-indigo-500",
            description: "Alcance y limitaciones del análisis"
        }
    ];

    const faqs = {
        general: [
            {
                question: "¿Qué es DogScan AI y cómo funciona?",
                answer: "DogScan AI es una plataforma que utiliza algoritmos de análisis de imágenes para evaluar la salud de perros. Nuestro sistema procesa fotografías de tu mascota para identificar posibles indicadores de problemas de salud. La tecnología está basada en un conjunto de datos de más de 50,000 imágenes de perros y ha sido desarrollada en colaboración con médicos veterinarios certificados. Los resultados proporcionan una evaluación preliminar que debe ser confirmada por un profesional veterinario."
            },
            {
                question: "¿Cuánto tiempo tarda en procesar las imágenes?",
                answer: "El sistema procesa las imágenes en aproximadamente 1-2 minutos, dependiendo de la calidad y cantidad de fotografías proporcionadas. Los resultados se almacenan de forma segura en tu cuenta para que puedas consultarlos cuando lo necesites, facilitando el seguimiento de la salud de tu mascota a lo largo del tiempo."
            },
            {
                question: "¿Cómo puedo comenzar a usar la plataforma?",
                answer: "Para comenzar, debes registrarte en nuestra plataforma y subir fotografías claras de tu perro desde diferentes ángulos. El sistema está optimizado para reconocer razas comunes en México y condiciones de salud frecuentes en la región. Recomendamos proporcionar al menos 3 fotografías diferentes para obtener resultados más precisos."
            }
        ],
        tech: [
            {
                question: "¿Cuál es la precisión del análisis?",
                answer: "Nuestro sistema tiene una precisión documentada del 85-90% para identificar características físicas visibles y posibles indicadores de problemas de salud comunes. Esta precisión ha sido validada mediante estudios comparativos con diagnósticos veterinarios profesionales. Es importante destacar que DogScan AI no reemplaza la evaluación de un médico veterinario, sino que funciona como una herramienta complementaria de detección temprana."
            },
            {
                question: "¿Qué tipo de fotografías debo proporcionar?",
                answer: "Para obtener resultados óptimos, recomendamos subir 3-5 fotografías claras desde diferentes ángulos (frontal, lateral y completo). La iluminación natural es ideal para capturar detalles importantes. Las imágenes deben mostrar claramente a tu mascota, preferiblemente sobre un fondo neutro y con buena iluminación. Las fotografías tomadas con un smartphone moderno son perfectamente adecuadas para el análisis."
            },
            {
                question: "¿Cómo protegen la información de mi mascota?",
                answer: "Implementamos protocolos de seguridad de nivel bancario, incluyendo encriptación AES-256 para todos los datos almacenados y transmitidos. Cumplimos con las normativas mexicanas de protección de datos personales (Ley Federal de Protección de Datos Personales en Posesión de Particulares). Las imágenes y datos de tu mascota se utilizan exclusivamente para proporcionar el servicio solicitado y, con tu consentimiento explícito, para mejorar nuestros algoritmos."
            }
        ],
        billing: [
            {
                question: "¿Qué planes ofrecen y cuáles son sus características?",
                answer: "Ofrecemos tres planes diseñados para diferentes necesidades:<br><br><strong>Plan Básico ($49/mes)</strong>: Incluye 5 análisis mensuales, evaluación de características físicas visibles y acceso a recursos educativos sobre cuidado canino.<br><br><strong>Plan Pro ($99/mes)</strong>: Incluye 15 análisis mensuales, evaluaciones más detalladas y acceso prioritario a consultas con veterinarios certificados.<br><br><strong>Plan Premium ($149/mes)</strong>: Ofrece análisis ilimitados, evaluaciones completas y acceso 24/7 a consultas veterinarias. Ideal para profesionales y hogares con múltiples mascotas.<br><br>Todos nuestros planes incluyen almacenamiento seguro del historial de análisis y actualizaciones regulares de la plataforma. Ofrecemos un descuento del 20% en pagos anuales."
            },
            {
                question: "¿Puedo cancelar mi suscripción cuando lo necesite?",
                answer: "Sí, puedes cancelar tu suscripción en cualquier momento desde tu perfil de usuario. No existen períodos de permanencia obligatorios ni penalizaciones por cancelación. El servicio permanecerá activo hasta el final del período facturado. Guardamos tu historial de análisis durante 6 meses después de la cancelación, por si decides reactivar tu cuenta."
            },
            {
                question: "¿Qué métodos de pago aceptan?",
                answer: "Aceptamos los siguientes métodos de pago:<br><br><strong>Tarjetas de crédito y débito</strong>: Visa, Mastercard y American Express.<br><strong>Efectivo en OXXO</strong>: Generamos un código de pago que puedes utilizar en cualquier tienda OXXO.<br><br>Todos nuestros métodos de pago utilizan protocolos de seguridad PCI DSS y están protegidos por certificación SSL. La activación del servicio es inmediata con tarjeta y puede demorar hasta 24 horas con pago en OXXO."
            }
        ],
        medical: [
            {
                question: "¿Qué condiciones de salud puede identificar el sistema?",
                answer: "El sistema puede identificar indicadores visuales de aproximadamente 30 condiciones comunes en perros, incluyendo:<br><br>- Problemas dermatológicos como alergias, dermatitis y parásitos externos<br>- Indicadores de problemas oculares como conjuntivitis y cataratas incipientes<br>- Signos de problemas dentales y de encías<br>- Indicadores de sobrepeso u obesidad<br>- Posibles signos de deshidratación<br><br>Es fundamental entender que DogScan AI proporciona evaluaciones preliminares basadas en características visibles, no diagnósticos definitivos. Cualquier hallazgo debe ser confirmado por un médico veterinario calificado."
            },
            {
                question: "¿Qué debo hacer si el sistema detecta un posible problema?",
                answer: "Si el sistema identifica posibles indicadores de problemas de salud, te proporcionaremos:<br><br>1. Una descripción clara de los hallazgos y su nivel de confianza<br>2. Recursos educativos relacionados con la posible condición<br>3. Recomendaciones sobre cuándo consultar a un veterinario<br>4. La opción de programar una consulta con uno de nuestros veterinarios certificados<br><br>Para casos que requieran atención inmediata, te proporcionaremos información sobre clínicas veterinarias cercanas a tu ubicación. Siempre recomendamos consultar a un profesional veterinario para confirmar cualquier hallazgo."
            },
            {
                question: "¿Cuáles son las credenciales de los veterinarios en su plataforma?",
                answer: "Todos nuestros médicos veterinarios cumplen con los siguientes requisitos:<br><br>- Título en Medicina Veterinaria y Zootecnia de instituciones acreditadas como la UNAM o el IPN<br>- Cédula Profesional vigente emitida por la Dirección General de Profesiones<br>- Mínimo de 3 años de experiencia clínica<br>- Capacitación continua verificable<br><br>Muchos de nuestros veterinarios tienen experiencia específica con razas populares en México como Chihuahua, Xoloitzcuintle y Schnauzer. Nuestro proceso de selección incluye verificación de credenciales, entrevistas y evaluación de habilidades clínicas."
            }
        ]
    };

    // Function to toggle FAQ open state
    const toggleFAQ = (categoryId, index) => {
        setOpenFAQs(prev => {
            const key = `${categoryId}-${index}`;
            return {
                ...prev,
                [key]: !prev[key]
            };
        });
    };

    // Function to handle category change
    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        // We don't reset openFAQs here, so they stay open when switching back
    };

    // Function to expand all FAQs in the current category
    const expandAllFAQs = () => {
        const newOpenFAQs = { ...openFAQs };
        
        faqs[selectedCategory]
            .filter(faq => 
                searchQuery === '' || 
                faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .forEach((_, index) => {
                const key = `${selectedCategory}-${index}`;
                newOpenFAQs[key] = true;
            });
        
        setOpenFAQs(newOpenFAQs);
    };

    // Function to collapse all FAQs in the current category
    const collapseAllFAQs = () => {
        const newOpenFAQs = { ...openFAQs };
        
        faqs[selectedCategory]
            .filter(faq => 
                searchQuery === '' || 
                faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .forEach((_, index) => {
                const key = `${selectedCategory}-${index}`;
                newOpenFAQs[key] = false;
            });
        
        setOpenFAQs(newOpenFAQs);
    };

    const FAQItem = ({ question, answer, isOpen, onToggle }) => {
        return (
            <motion.div
                initial={false}
                animate={{ backgroundColor: isOpen ? "rgba(251, 243, 219, 0.4)" : "transparent" }}
                className="border border-amber-100/70 rounded-xl overflow-hidden
                         transition-all duration-200 hover:border-amber-200/70"
            >
                <motion.button
                    onClick={onToggle}
                    className="w-full py-4 sm:py-5 px-5 sm:px-6 flex justify-between items-center text-left
                             group transition-all duration-200"
                >
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className={`p-2 rounded-lg transition-colors duration-200
                                    ${isOpen ? 'bg-amber-100/80' : 'bg-amber-50/50'}`}>
                            <HelpCircle className={`h-5 w-5 transition-colors duration-200
                                                ${isOpen ? 'text-amber-700' : 'text-amber-500'}`} />
                        </div>
                        <span className={`font-medium transition-colors duration-200 text-base sm:text-lg
                                     ${isOpen ? 'text-amber-900' : 'text-amber-800'}`}>
                            {question}
                        </span>
                    </div>
                    <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        className={`flex-shrink-0 ml-4 transition-colors duration-200
                                ${isOpen ? 'text-amber-600' : 'text-amber-400'}`}
                    >
                        <ChevronDown className="h-5 w-5 sm:h-6 sm:w-6" />
                    </motion.div>
                </motion.button>
                <AnimatePresence initial={false} mode="sync">
                    {isOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            className="overflow-hidden border-t border-amber-100/50"
                        >
                            <div className="px-5 sm:px-6 py-4 sm:py-5 text-amber-700 text-sm sm:text-base leading-relaxed">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0 w-1 rounded-full bg-gradient-to-b from-amber-300/50 to-orange-300/50" />
                                    <div dangerouslySetInnerHTML={{ __html: answer }} />
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        );
    };

    return (
        <section className="relative overflow-hidden py-12 sm:py-16 lg:py-20" id="faq">
            {/* Enhanced Background Pattern */}
            <div className="absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50/95 via-orange-50/95 to-white" />
                <div className="absolute inset-0">
                    <motion.div 
                        className="absolute top-1/4 right-[15%] w-64 h-64 bg-amber-200/20 rounded-full blur-3xl"
                        animate={{
                            scale: [1, 1.1, 1],
                            opacity: [0.3, 0.4, 0.3]
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "linear"
                        }}
                    />
                    <motion.div 
                        className="absolute bottom-1/3 left-[10%] w-72 h-72 bg-orange-200/20 rounded-full blur-3xl"
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.2, 0.3, 0.2]
                        }}
                        transition={{
                            duration: 10,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "linear"
                        }}
                    />
                </div>
                <div className="absolute inset-0 opacity-5">
                    <PawPrint className="absolute h-24 sm:h-32 lg:h-40 w-24 sm:w-32 lg:w-40 
                                     -right-6 top-12 text-amber-900 rotate-12" />
                    <PawPrint className="absolute h-20 sm:h-24 lg:h-32 w-20 sm:w-24 lg:w-32 
                                     -left-4 bottom-8 text-amber-900 -rotate-12" />
                    <Heart className="absolute h-16 sm:h-20 lg:h-24 w-16 sm:w-20 lg:w-24 
                                  right-[20%] bottom-[20%] text-amber-900/10" />
                </div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative max-w-4xl">
                {/* Enhanced Header Section */}
                <div className="text-center mb-10 sm:mb-12 lg:mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm 
                               px-4 py-2 rounded-full shadow-sm border border-amber-100/50 mb-6"
                    >
                        <Shield className="h-5 w-5 text-amber-600" />
                        <span className="text-base sm:text-lg font-medium bg-gradient-to-r from-amber-600 to-orange-600 
                                     text-transparent bg-clip-text">
                            Información Verificada
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-900 mb-4 sm:mb-6"
                    >
                        Información sobre el cuidado de tu mascota
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-base sm:text-lg lg:text-xl text-amber-700 max-w-2xl mx-auto mb-8 sm:mb-10"
                    >
                        Encuentra respuestas precisas a tus preguntas sobre DogScan AI. 
                        Nuestra plataforma utiliza tecnología de análisis de imágenes para ayudarte 
                        a monitorear la salud de tu mascota con el respaldo de veterinarios certificados.
                    </motion.p>

                    {/* Enhanced Category Tabs */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-12"
                    >
                        {categories.map((category) => {
                            const Icon = category.icon;
                            const isSelected = selectedCategory === category.id;
                            
                            return (
                                <motion.button
                                    key={category.id}
                                    onClick={() => handleCategoryChange(category.id)}
                                    className={`relative group p-4 sm:p-5 rounded-xl border transition-all duration-200
                                             ${isSelected 
                                                ? 'bg-white shadow-lg border-amber-200/70' 
                                                : 'bg-white/60 border-amber-100/50 hover:bg-white hover:border-amber-200/50 hover:shadow-md'}`}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className="flex flex-col items-center text-center">
                                        <div className={`p-3 rounded-full mb-3 bg-gradient-to-br ${category.color}`}>
                                            <Icon className="h-5 w-5 text-white" />
                                        </div>
                                        <h3 className="font-medium text-amber-900 mb-1">{category.label}</h3>
                                        <p className="text-xs text-amber-700/80">{category.description}</p>
                                        
                                        {isSelected && (
                                            <motion.div 
                                                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-b-xl"
                                                layoutId="activeCategory"
                                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                            />
                                        )}
                                    </div>
                                </motion.button>
                            );
                        })}
                    </motion.div>

                    {/* Expand/Collapse Controls */}
                    <div className="flex justify-end mb-4">
                        <div className="flex space-x-2">
                            <button 
                                onClick={expandAllFAQs}
                                className="text-sm flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors border border-amber-200/50"
                            >
                                <ChevronDown className="h-4 w-4" />
                                <span>Expandir todo</span>
                            </button>
                            <button 
                                onClick={collapseAllFAQs}
                                className="text-sm flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors border border-amber-200/50"
                            >
                                <ChevronUp className="h-4 w-4" />
                                <span>Colapsar todo</span>
                            </button>
                        </div>
                </div>

                {/* FAQ Items */}
                    <div className="space-y-4 sm:space-y-5">
                        {faqs[selectedCategory]
                            .filter(faq => 
                                searchQuery === '' || 
                                faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map((faq, index) => {
                                const faqKey = `${selectedCategory}-${index}`;
                                return (
                        <motion.div
                                        key={faqKey}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <FAQItem 
                                            question={faq.question} 
                                            answer={faq.answer} 
                                            isOpen={!!openFAQs[faqKey]}
                                            onToggle={() => toggleFAQ(selectedCategory, index)}
                                        />
                                    </motion.div>
                                );
                            })}
                        
                        {faqs[selectedCategory].filter(faq => 
                            searchQuery === '' || 
                            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
                        ).length === 0 && (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-10"
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                                    <Search className="h-8 w-8 text-amber-500" />
                                </div>
                                <h3 className="text-lg font-medium text-amber-900 mb-2">No encontramos resultados</h3>
                                <p className="text-amber-700">Intenta con otra búsqueda o categoría</p>
                        </motion.div>
                        )}
                    </div>
                </div>

                {/* Enhanced Support CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-12 sm:mt-16 text-center"
                >
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 sm:p-8
                                border border-amber-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center justify-center mb-4">
                            <div className="bg-white p-3 rounded-full shadow-sm">
                                <HeartPulse className="h-8 w-8 text-amber-600" />
                            </div>
                        </div>
                        <h3 className="text-xl sm:text-2xl font-bold text-amber-900 mb-2">
                            ¿Necesitas ayuda personalizada?
                        </h3>
                        <p className="text-amber-700 mb-6 max-w-md mx-auto">
                            Nuestro equipo de médicos veterinarios con cédula profesional está disponible para resolver tus dudas
                            y brindarte orientación específica para el cuidado de tu mascota.
                    </p>
                    <Button
                            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 
                                     hover:to-orange-600 text-white px-6 py-3 text-base sm:text-lg rounded-xl
                                     shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                        onClick={() => window.open(`https://wa.me/525643251394`, '_blank')}
                    >
                            <MessageCircle className="mr-2 h-5 w-5" />
                            Contactar a un veterinario
                    </Button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default FAQSection;